import React, { useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Drawer as MantineDrawer,
  Tabs as MantineTabs,
  Select,
  Title,
  createStyles,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import PackageStatusBadge from 'components/status-badges/PackageStatusBadge';
import { useAuth } from 'context/auth-context';
import useAthletePackage from 'hooks/athletePackage/useAthletePackage';

import ActionsMenu from './components/ActionsMenu';
import AttendanceTab from './components/AttendanceTab';
import CreditsTab from './components/CreditsTab';
import InvoicesTab from './components/InvoicesTab';
import SummaryTab from './components/SummaryTab/SummaryTab';

interface IAthletePackageDetailProps {
  opened: boolean;
  closeHandler: () => void;
  setAthletePackageId: React.Dispatch<React.SetStateAction<string | undefined>>;
  athletePackageId?: string;
  permission?: boolean;
}

const useStyles = createStyles(theme => ({
  select: {
    padding: theme.spacing.xs,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
}));

const AthletePackageDetail = ({
  opened,
  closeHandler,
  setAthletePackageId,
  athletePackageId,
  permission = true,
}: IAthletePackageDetailProps): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const [activeTab, setActiveTab] = useState<string | null>('credits');
  const { data, isLoading } = useAthletePackage(athletePackageId);
  const { hasPerm } = useAuth();
  const eventMutationPerm = hasPerm('EventMutate');
  const invoiceAthleteViewPerm = hasPerm('InvoiceAthleteView');

  const tabs = useMemo(
    () => [
      {
        label: 'Credits',
        value: 'credits',
        component: data?.data ? <CreditsTab athletePackage={data.data} permission={permission} /> : <div />,
        tabProps: { 'data-test': 'credits' },
      },
      {
        label: 'Payments / Invoices',
        value: 'payments-invoices',
        component: data?.data ? <InvoicesTab athletePackage={data.data} permission={permission} /> : <div />,
        tabProps: { 'data-test': 'payments-invoices' },
        disabled: !invoiceAthleteViewPerm,
      },
      {
        label: 'Attendance',
        value: 'attendance',
        component: data?.data ? (
          <AttendanceTab athletePackage={data.data} permission={eventMutationPerm} />
        ) : (
          <div />
        ),
        tabProps: { 'data-test': 'attendance-tab' },
      },
      {
        label: 'Summary',
        value: 'summary',
        component: data?.data ? (
          <SummaryTab athletePackage={data?.data} setAthletePackageId={setAthletePackageId} />
        ) : (
          <div />
        ),
        tabProps: { 'data-test': 'summary-tab' },
      },
    ],
    [data?.data, permission, setAthletePackageId, eventMutationPerm, invoiceAthleteViewPerm],
  );

  const header = (
    <Group position='apart' align='center' noWrap>
      <Group spacing='xs'>
        <Title order={3} mr='lg'>
          {data?.data.package.name}
        </Title>
        {data?.data.status && (
          <PackageStatusBadge
            status={data.data.status}
            isCancelEarly={
              data.data.cancellationDate !== null &&
              data.data.status !== 'CANCELLED' &&
              data.data.status !== 'ENDED'
            }
            fullWidth={false}
            size='sm'
          />
        )}
        {data?.data.autoRenew ? (
          <Badge size='sm' color='green'>
            Auto-renew: On
          </Badge>
        ) : (
          <Badge size='sm' color='gray'>
            Auto-renew: Off
          </Badge>
        )}
      </Group>

      <MantineDrawer.CloseButton />
    </Group>
  );

  return (
    <Drawer
      opened={opened}
      onClose={closeHandler}
      size={1200}
      header={header}
      padding='0'
      closeOnClickOutside
      footer={
        <Group position='right' p={theme.spacing.lg}>
          <Button variant='subtle' onClick={closeHandler}>
            Close
          </Button>
        </Group>
      }
    >
      <LoadingOverlay visible={isLoading} />
      <MantineTabs value={activeTab} onTabChange={setActiveTab}>
        {isDesktop ? (
          <MantineTabs.List
            mb='md'
            p={`calc(${theme.fontSizes.xl} * 1.50)`}
            pb={0}
            pt={`calc(${theme.fontSizes.xl} * .75)`}
          >
            {tabs.map(item => (
              <MantineTabs.Tab
                {...item.tabProps}
                key={item.value}
                value={item.value}
                disabled={item.disabled}
              >
                {item.label}
              </MantineTabs.Tab>
            ))}
            {data?.data && (
              <ActionsMenu athletePackage={data.data} disabled={!permission} closeDrawer={closeHandler} />
            )}
          </MantineTabs.List>
        ) : (
          <div className={classes.select}>
            <Select
              value={activeTab}
              onChange={setActiveTab}
              data={tabs}
              rightSection={<IconChevronDown />}
              styles={{
                input: {
                  border: 'none',
                  padding: 0,
                  fontWeight: 700,
                  fontSize: theme.fontSizes.md,
                  background: 'none',
                },
              }}
              dropdownPosition='bottom'
            />
            {data?.data && (
              <ActionsMenu athletePackage={data.data} disabled={!permission} closeDrawer={closeHandler} />
            )}
          </div>
        )}
        {tabs.map(item => (
          <MantineTabs.Panel key={item.value} value={item.value} p='xl'>
            {item.component}
          </MantineTabs.Panel>
        ))}
      </MantineTabs>
    </Drawer>
  );
};

export default AthletePackageDetail;
